// Preview screen (`/preview`) — README §6.4.
//
// A read-only walkthrough of the cook: every PreCook / PrepCook / LiveCook
// step shown so the user can read ahead, follow chef-hat links and open help
// panels BEFORE committing. It is not gated on prerequisites, makes no
// /anchor call and runs no live clock — the real cook still begins only via
// Start Cooking on Prereqs.
//
// Chips name the file's phases and navigate between screens: a combined
// Pre/PrepCook screen when both prep phases are declared, otherwise the
// individual phase, then LiveCook. Times are calculated once, when Preview
// is displayed: anchored to the Planner's serve time if one was set, else
// to the present moment (see previewProjection).
//
// Locked to landscape on phones (rotation prompt, README §6.7).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Wordmark } from '../design/components';
import { theme } from '../design/theme';
import { useAppStore } from '../state/store';
import { PreviewPane } from '../cooking/PreviewPane';
import { buildPreviewPhases, groupPreviewScreens } from '../cooking/previewProjection';
import { useShouldPromptRotation } from '../cooking/useShouldPromptRotation';

export default function Preview() {
  const navigate = useNavigate();
  const recipe = useAppStore((s) => s.recipe);
  const recipeFile = useAppStore((s) => s.recipeFile);
  const serveTime = useAppStore((s) => s.serveTime);

  // Capture "now" once on mount — Preview's times are a static read-out, not
  // a ticking clock. Used only when no serve time is set.
  const [displayStart] = useState(() => Date.now());

  const screens = useMemo(() => {
    if (!recipe) return [];
    return groupPreviewScreens(buildPreviewPhases(recipe, serveTime, displayStart));
  }, [recipe, serveTime, displayStart]);

  const [selected, setSelected] = useState<string>('');
  const portrait = useShouldPromptRotation();

  if (!recipe) {
    return (
      <main style={{ padding: '1rem' }}>
        <Wordmark size="md" as="h1" />
        <p style={{ color: theme.text.muted }}>Pick a recipe first.</p>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </main>
    );
  }

  const activeId = screens.some((s) => s.id === selected) ? selected : (screens[0]?.id ?? '');
  const activeScreen = screens.find((s) => s.id === activeId);

  if (portrait) {
    return (
      <main
        aria-label="Rotation prompt"
        style={{
          padding: '2rem 1rem',
          background: theme.bg.base,
          color: theme.text.primary,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Wordmark size="md" as="h1" />
        <p style={{ maxWidth: '20rem', fontSize: '1.1rem' }}>
          Please rotate your phone to landscape.
        </p>
        <p style={{ maxWidth: '20rem', color: theme.text.muted }}>Preview needs the wider view.</p>
      </main>
    );
  }

  return (
    <main
      aria-label="Preview"
      style={{
        background: theme.bg.base,
        color: theme.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        padding: '1rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
          <Wordmark size="sm" as="span" />
          <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>Preview</span>
          <span style={{ color: theme.text.muted, fontSize: '0.9rem' }}>
            {recipeFile?.name ?? 'Read through before you cook.'}
          </span>
        </div>
      </div>

      <p style={{ margin: 0, color: theme.text.muted, fontSize: '0.9rem' }}>
        Reading ahead — nothing is cooking. Use the chips to move between phases; tap the chef-hat
        for help.
      </p>

      <nav aria-label="Preview phases" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {screens.map((s) => {
          const isActive = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => setSelected(s.id)}
              style={{
                padding: '0.35rem 0.9rem',
                borderRadius: '999px',
                fontSize: '0.9rem',
                fontWeight: 600,
                lineHeight: 1.2,
                cursor: 'pointer',
                background: isActive ? theme.accent.action : theme.bg.raised,
                color: isActive ? theme.bg.base : theme.text.primary,
                border: '1px solid transparent',
                minHeight: '36px',
              }}
            >
              {s.chipLabel}
            </button>
          );
        })}
      </nav>

      {activeScreen && <PreviewPane screen={activeScreen} />}

      <div style={{ marginTop: '0.5rem' }}>
        <Button variant="secondary" onClick={() => navigate('/prereqs')}>
          Back to Prerequisites
        </Button>
      </div>
    </main>
  );
}
